import { useCallback, useState } from 'react';
import { classNames } from 'primereact/utils';
import { DownChevronIcon } from '@repo/atomic-components';
import { ToolkitGroupCollection } from '@/api/toolkit';
import { useResizeObserver } from '@/hooks';
import { CollectionMatchCounts } from '@/store';

interface ToolkitTabsProps {
  collections: ToolkitGroupCollection[];
  activeCollectionTitle: string | null;
  collectionMatchCounts: CollectionMatchCounts;
  onCollectionChange: (collectionTitle: string) => void;
}

type ScrollEdges = { start: boolean; end: boolean };

export const ToolkitTabs = ({
  collections,
  activeCollectionTitle,
  collectionMatchCounts,
  onCollectionChange,
}: ToolkitTabsProps) => {
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const [track, setTrack] = useState<HTMLDivElement | null>(null);
  // Which ends still have tabs scrolled out of view.
  const [edges, setEdges] = useState<ScrollEdges>({ start: false, end: false });

  const updateEdges = useCallback(() => {
    if (!scroller) return;
    const start = scroller.scrollLeft > 1;
    const end = scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1;
    setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, [scroller]);

  useResizeObserver(scroller, undefined, updateEdges);
  useResizeObserver(track, undefined, updateEdges);

  const scrollBy = (direction: 1 | -1) => {
    scroller?.scrollBy({ left: direction * scroller.clientWidth * 0.6, behavior: 'smooth' });
  };

  if (collections.length === 0) {
    return null;
  }

  // Mouse-only helpers: keyboard focus already scrolls a tab into view.
  const renderScrollButton = (direction: 1 | -1) => (
    <button
      type="button"
      tabIndex={-1}
      aria-hidden
      onClick={() => scrollBy(direction)}
      className={classNames(
        'from-theme-white via-theme-white text-theme-black hover:text-theme-dark-green absolute top-0 bottom-0.5 z-[1] flex w-12 cursor-pointer items-center to-transparent pb-2',
        direction === -1 ? 'left-0 justify-start bg-gradient-to-r' : 'right-0 justify-end bg-gradient-to-l',
      )}
    >
      <DownChevronIcon className={direction === -1 ? 'rotate-90' : '-rotate-90'} width={12} height={12} />
    </button>
  );

  return (
    <div
      role="tablist"
      aria-label="Toolkit collections"
      className="border-theme-border-grey relative w-full min-w-0 shrink-0 border-b"
    >
      {edges.start && renderScrollButton(-1)}
      <div ref={setScroller} onScroll={updateEdges} className="hide-scrollbar w-full min-w-0 overflow-x-auto">
        <div ref={setTrack} className="flex w-max items-center gap-6">
          {collections.map((collection) => {
            const isActive = collection.collectionTitle === activeCollectionTitle;
            const matchCount = collectionMatchCounts[collection.collectionTitle] ?? 0;
            const showBadge = !isActive && matchCount > 0;

            return (
              <button
                key={collection.collectionTitle}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onCollectionChange(collection.collectionTitle)}
                className={classNames(
                  'relative flex shrink-0 cursor-pointer items-center gap-[10px] border-0 border-b-2 bg-transparent px-6 pb-2 text-sm whitespace-nowrap outline-transparent transition-colors',
                  'focus-visible:outline-theme-dark-green focus-visible:outline-2',
                  {
                    'border-theme-dark-green text-theme-dark-green font-semibold': isActive,
                    'text-theme-black hover:text-theme-dark-green border-transparent font-medium': !isActive,
                  },
                )}
              >
                <span>{collection.collectionTitle}</span>
                {showBadge && (
                  <span
                    aria-label={`${matchCount} matches`}
                    className="bg-fletchers-green-500 inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full px-1 text-[0.6875rem] leading-none font-semibold text-white"
                  >
                    {matchCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {edges.end && renderScrollButton(1)}
    </div>
  );
};
